import React, { useState, useEffect, useCallback } from 'react';
import { Calculator, TrendingUp } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DatePreset, DATE_PRESETS } from '@/types/filters';
import { getDateRangeForPreset, formatDateForInput } from '@/utils/dateUtils';
import { UsageMetrics, metricsService } from '@/services/metricsService';
import { userActivityService } from '@/services/userActivityService';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { db } from '@/config/firebase';
import { collection, query, where, orderBy, getDocs } from 'firebase/firestore';

interface UsageMetricsSectionProps {
  // No props needed
}

const UsageMetricsSection: React.FC<UsageMetricsSectionProps> = () => {
  const { toast } = useToast();
  
  // State for metrics and loading
  const [metrics, setMetrics] = useState<UsageMetrics | null>(null);
  const [dailyMetrics, setDailyMetrics] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isRecalculating, setIsRecalculating] = useState(false);
  
  // State for date range
  const [dateRange, setDateRange] = useState(() => {
    // Get March 1, 2025 as the minimum start date
    const minStartDate = new Date('2025-03-01');
    // Get current date for end date
    const today = new Date();
    
    // Calculate 30 days ago
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(today.getDate() - 30);
    
    // Use March 1, 2025 or 30 days ago, whichever is later
    const startDate = thirtyDaysAgo > minStartDate ? thirtyDaysAgo : minStartDate;
    
    return {
      start: startDate.toISOString().split('T')[0],
      end: today.toISOString().split('T')[0]
    };
  });
  
  // State for date preset
  const [selectedPreset, setSelectedPreset] = useState<DatePreset>(DATE_PRESETS.ALL_DATES);
  
  // Helper to normalize and format app names
  const getAppName = (appId: string): string => {
    // Comprehensive app name mapping
    const appNames: Record<string, string> = {
      // Core Apps
      'hub': 'Apps Hub',
      'good-things': 'Good Things',
      
      // Amazon Tools
      'sb-search-term-report': 'SB Search Term Report',
      'asin-sales-history': 'ASIN Sales History',
      'amazon-index-checker': 'Amazon Index Checker',
      'pmt2': 'PMT2',
      'pmt3': 'PMT3',
      'ppc-farm': 'PPC Farm',
      'primary-ledger': 'Primary Ledger',
      'days-of-cover': 'Days of Cover',
      'farming-results': 'Farming Results',
      
      // Management Tools
      'plms': 'PLMS',
      'sop': 'SOP Central',
      'employee-directory': 'Employee Directory',
      'product-manager': 'Product Manager',
      
      // Other Tools
      'dayaway': 'Day Away Board',
      'day-away': 'Day Away Board',
      'ideogram': 'Ideogram',
      'glossary': 'Glossary',
      'inventory': 'Inventory',
      'idea-machine': 'Idea Machine',
      'lms': 'LMS',
      'smart-writer': 'Smart Writer',
      'ai-dev-quest': 'AI Dev Quest'
    };
    
    // If we have a direct mapping, use it
    if (appNames[appId]) {
      return appNames[appId];
    }
    
    // Otherwise, auto-format: convert hyphens to spaces and capitalize each word
    return appId
      .split(/[-_]/)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Load daily metrics for the trend chart
  const loadDailyMetrics = useCallback(async () => {
    try {
      const metricsQuery = query(
        collection(db, 'usageMetrics'),
        where('date', '>=', dateRange.start),
        where('date', '<=', dateRange.end),
        orderBy('date', 'asc')
      );
      
      const snapshot = await getDocs(metricsQuery);
      
      // Calculate daily deltas for Good Things
      let previousTotal = 0;
      
      const daily = snapshot.docs.map((doc, index) => {
        const data = doc.data();
        const currentTotal = data.goodThings?.total || 0;
        
        // Calculate delta from previous day
        let newSubmissions = 0;
        if (index > 0) {
          newSubmissions = Math.max(0, currentTotal - previousTotal);
        }
        
        previousTotal = currentTotal;

        return {
          date: data.date,
          users: data.activeUsers?.total || 0,
          dailySubmissions: newSubmissions
        };
      });
      
      setDailyMetrics(daily);
    } catch (error) {
      console.error('Error loading daily metrics:', error);
    }
  }, [dateRange.start, dateRange.end]);
  
  // Load metrics for the current date range
  const loadMetrics = useCallback(async () => {
    try {
      setIsLoading(true);
      
      // Import and temporarily disable rate limiter
      const { rateLimiter } = await import('@/services/rateLimiter');
      const wasEnabled = rateLimiter.getStatus().enabled;
      const wasEmergencyMode = rateLimiter.getStatus().emergencyMode;
      
      if (wasEnabled) {
        console.log('Temporarily disabling rate limiter for metrics fetch');
        rateLimiter.disable();
        if (wasEmergencyMode) {
          rateLimiter.disableEmergencyMode();
        }
      }
      
      try {
        const data = await metricsService.getMetrics(dateRange.start, dateRange.end);
        setMetrics(data);
        console.log('Metrics loaded successfully:', data);
        
        // Also fetch daily breakdown for the chart
        await loadDailyMetrics();
      } finally {
        // Re-enable rate limiter if it was enabled before
        if (wasEnabled) {
          console.log('Re-enabling rate limiter');
          rateLimiter.enable();
          if (wasEmergencyMode) {
            rateLimiter.enableEmergencyMode();
          }
        }
      }
    } catch (error) {
      console.error('Error loading metrics:', error);
      toast({
        title: "Error Loading Metrics",
        description: "Failed to load metrics. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  }, [dateRange.start, dateRange.end, loadDailyMetrics, toast]);
  
  // Load metrics when component mounts or date range changes
  useEffect(() => {
    loadMetrics();
  }, [loadMetrics]);
  
  // Handle preset selection
  const handlePresetChange = (value: string) => {
    const preset = value as DatePreset;
    setSelectedPreset(preset);
    
    // Handle custom rolling windows that aren't in standard presets
    const today = new Date();
    let start = new Date();
    
    if (value === 'last_7_days') {
      start.setDate(today.getDate() - 7);
      setDateRange({
        start: formatDateForInput(start),
        end: formatDateForInput(today)
      });
      return;
    }
    
    if (value === 'last_30_days') {
      start.setDate(today.getDate() - 30);
      setDateRange({
        start: formatDateForInput(start),
        end: formatDateForInput(today)
      });
      return;
    }

    // Apply standard presets
    const range = getDateRangeForPreset(preset);
    const startDate = formatDateForInput(range.from);
    const endDate = formatDateForInput(range.to);
    
    // Update the date range
    setDateRange({ start: startDate, end: endDate });
  };
  
  // Calculate percentages for Good Things submissions
  const calculatePercentages = () => {
    if (!metrics || metrics.goodThings.total === 0) {
      return { appPercent: 0, sheetsPercent: 0 };
    }
    
    const appPercent = Math.round((metrics.goodThings.fromApp / metrics.goodThings.total) * 100);
    const sheetsPercent = Math.round((metrics.goodThings.fromSheets / metrics.goodThings.total) * 100);
    
    return { appPercent, sheetsPercent };
  };
  
  const { appPercent, sheetsPercent } = calculatePercentages();
  
  // Date preset options
  const datePresetOptions = [
    { value: DATE_PRESETS.ALL_DATES, label: 'Lifetime' },
    { value: 'last_7_days', label: 'Last 7 Days' },
    { value: DATE_PRESETS.THIS_MONTH, label: 'This Month' },
    { value: DATE_PRESETS.LAST_MONTH, label: 'Last Month' },
    { value: DATE_PRESETS.THIS_YEAR, label: 'This Year' },
    { value: DATE_PRESETS.LAST_YEAR, label: 'Last Year' },
    { value: DATE_PRESETS.DATE_RANGE, label: 'Custom Dates' }
  ];
  
  // Handle recalculation
  const handleRecalculate = async () => {
    try {
      setIsRecalculating(true);
      
      // Import and temporarily disable rate limiter
      const { rateLimiter } = await import('@/services/rateLimiter');
      const wasEnabled = rateLimiter.getStatus().enabled;
      const wasEmergencyMode = rateLimiter.getStatus().emergencyMode;
      
      if (wasEnabled) {
        console.log('Temporarily disabling rate limiter for metrics recalculation');
        rateLimiter.disable();
        if (wasEmergencyMode) {
          rateLimiter.disableEmergencyMode();
        }
      }
      
      try {
        // Get dates in the selected range
        let start = new Date(dateRange.start);
        const end = new Date(dateRange.end);
        const minDate = new Date('2025-03-01');
        
        // Ensure start date is not before March 1, 2025
        if (start < minDate) {
          start = minDate;
          setDateRange(prev => ({ ...prev, start: '2025-03-01' }));
        }
        
        const dates: string[] = [];
        
        // Generate array of dates in YYYY-MM-DD format
        const currentDate = new Date(start);
        while (currentDate <= end) {
          dates.push(currentDate.toISOString().split('T')[0]);
          currentDate.setDate(currentDate.getDate() + 1);
        }
        
        // Recalculate metrics for each date
        for (const date of dates) {
          await userActivityService.recalculateMetricsForDate(date);
        }
        
        // Clear all caches to ensure fresh data
        userActivityService.clearMetricsCache();
        
        // Reload metrics
        await loadMetrics();
        
        toast({
          title: "Metrics Recalculated",
          description: `Successfully recalculated metrics for ${dates.length} day(s)`,
          variant: "default"
        });
      } finally {
        // Re-enable rate limiter if it was enabled before
        if (wasEnabled) {
          console.log('Re-enabling rate limiter');
          rateLimiter.enable();
          if (wasEmergencyMode) {
            rateLimiter.enableEmergencyMode();
          }
        }
      }
    } catch (error) {
      console.error('Error recalculating metrics:', error);
      toast({
        title: "Recalculation Failed",
        description: "Failed to recalculate metrics. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsRecalculating(false);
    }
  };

  return (
    <Card className="border-none shadow-none bg-transparent">
      <CardContent className="px-0">
        <div className="space-y-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <label className="block text-sm font-medium mb-2">Date Range</label>
              <div className="flex flex-col sm:flex-row gap-2 items-end">
                <div className="flex-1 w-full">
                  <label className="block text-xs text-muted-foreground mb-1">Start Date</label>
                  <input
                    type="date"
                    value={dateRange.start}
                    min="2025-03-01"
                    onChange={(e) => {
                      // Ensure date is not before March 1, 2025
                      const selectedDate = new Date(e.target.value);
                      const minDate = new Date('2025-03-01');
                      
                      let newStart = e.target.value;
                      if (selectedDate < minDate) {
                        newStart = '2025-03-01';
                      }
                      
                      // Update date range
                      setDateRange(prev => ({ ...prev, start: newStart }));
                      setSelectedPreset(DATE_PRESETS.DATE_RANGE);
                    }}
                    className="w-full h-[38px] p-2 border rounded-md bg-background"
                  />
                </div>
                <div className="flex-1 w-full">
                  <label className="block text-xs text-muted-foreground mb-1">End Date</label>
                  <input
                    type="date"
                    value={dateRange.end}
                    min="2025-03-01"
                    onChange={(e) => {
                      // Ensure date is not before March 1, 2025
                      const selectedDate = new Date(e.target.value);
                      const minDate = new Date('2025-03-01');
                      
                      let newEnd = e.target.value;
                      if (selectedDate < minDate) {
                        newEnd = '2025-03-01';
                      }
                      
                      // Update date range
                      setDateRange(prev => ({ ...prev, end: newEnd }));
                      setSelectedPreset(DATE_PRESETS.DATE_RANGE);
                    }}
                    className="w-full h-[38px] p-2 border rounded-md bg-background"
                  />
                </div>
                <div className="flex-1 w-full">
                  <label className="block text-xs text-muted-foreground mb-1">Filter</label>
                  <Select
                    value={selectedPreset}
                    onValueChange={handlePresetChange}
                  >
                    <SelectTrigger className="w-full h-[38px]">
                      <SelectValue placeholder="Select filter" />
                    </SelectTrigger>
                    <SelectContent>
                      {datePresetOptions.map(option => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="w-full sm:w-auto">
                  <label className="text-xs text-muted-foreground mb-1 opacity-0 hidden sm:block">Action</label>
                  <Button
                    onClick={handleRecalculate}
                    disabled={isLoading || isRecalculating}
                    className="flex items-center justify-center w-full sm:w-auto h-[38px] gap-2 bg-purple-600 hover:bg-purple-700 dark:bg-purple-500 dark:hover:bg-purple-600"
                  >
                    {isRecalculating ? (
                      <Calculator className="h-4 w-4 animate-spin" />
                    ) : (
                      <Calculator className="h-4 w-4" />
                    )}
                    {isRecalculating ? 'Recalculating...' : 'Recalculate'}
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Current Date Range Indicator */}
          <div className="bg-blue-50 dark:bg-blue-900/30 p-3 rounded-md text-sm">
            <span className="font-medium">Current Date Range:</span>{' '}
            <span className="text-blue-600 dark:text-blue-300">
              {dateRange.start} to {dateRange.end}
            </span>
            {selectedPreset !== DATE_PRESETS.DATE_RANGE && (
              <span className="ml-2 bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 px-2 py-0.5 rounded text-xs">
                {datePresetOptions.find(option => option.value === selectedPreset)?.label || 'Custom'}
              </span>
            )}
          </div>

          {metrics ? (
            <div className="space-y-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Active Users Card */}
                <div className="bg-card p-4 rounded-lg border shadow-sm">
                  <h3 className="text-lg font-medium mb-4">Active Users</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center py-2 border-b">
                      <span className="font-medium">Total Active Users</span>
                      <span className="text-blue-600 font-bold dark:text-blue-400">{metrics.activeUsers.total}</span>
                    </div>
                    
                    <h4 className="text-md font-medium mt-4">By App</h4>
                    {Object.entries(metrics.activeUsers.byApp)
                      .filter(([_, count]) => count > 0) // Only show apps with active users
                      .sort(([_, countA], [__, countB]) => (countB as number) - (countA as number)) // Sort by count descending
                      .map(([appId, count]) => (
                        <div key={appId} className="flex justify-between items-center py-1 border-b">
                          <span className="text-muted-foreground">{getAppName(appId)}</span>
                          <span className="text-blue-600 font-medium dark:text-blue-400">{count}</span>
                        </div>
                      ))}
                  </div>
                </div>

                {/* Good Things Submissions Card */}
                <div className="bg-card p-4 rounded-lg border shadow-sm">
                  <h3 className="text-lg font-medium mb-4">Good Things Submissions</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center py-2 border-b">
                      <span className="font-medium">Total Submissions</span>
                      <span className="text-blue-600 font-bold dark:text-blue-400">{metrics.goodThings.total}</span>
                    </div>
                    
                    <div className="flex justify-between items-center py-1 border-b">
                      <span className="text-muted-foreground">From App</span>
                      <div className="flex items-center gap-2">
                        <span className="text-blue-600 font-medium dark:text-blue-400">{metrics.goodThings.fromApp}</span>
                        <span className="text-xs text-muted-foreground">({appPercent}%)</span>
                      </div>
                    </div>
                    
                    <div className="flex justify-between items-center py-1 border-b">
                      <span className="text-muted-foreground">From Sheets</span>
                      <div className="flex items-center gap-2">
                        <span className="text-blue-600 font-medium dark:text-blue-400">{metrics.goodThings.fromSheets}</span>
                        <span className="text-xs text-muted-foreground">({sheetsPercent}%)</span>
                      </div>
                    </div>
                    
                    {/* Visual representation of the split */}
                    <div className="mt-4">
                      <div className="w-full bg-secondary rounded-full h-2.5 overflow-hidden">
                        <div
                          className={`h-2.5 rounded-full ${appPercent > 100 ? 'bg-red-500' : 'bg-blue-600 dark:bg-blue-500'}`}
                          style={{ width: `${Math.min(appPercent, 100)}%` }}
                        ></div>
                      </div>
                      <div className="flex justify-between text-xs mt-1">
                        <span className="text-muted-foreground">App: {appPercent}%</span>
                        <span className="text-muted-foreground">Sheets: {sheetsPercent}%</span>
                      </div>
                    </div>
                    
                    {(appPercent > 100 || metrics.goodThings.fromApp > metrics.goodThings.total) && (
                      <div className="mt-4 p-3 bg-amber-50 dark:bg-amber-900/30 rounded-md">
                        <p className="text-sm text-amber-800 dark:text-amber-200">
                          <span className="font-medium">⚠️ Data Cleanup Needed:</span> The numbers show corrupted accumulated data ({metrics.goodThings.fromApp} from app).
                          Click <strong>"Recalculate"</strong> above to reset this date range to 0. Going forward, new submissions from the app will be tracked correctly.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Usage Trends Chart */}
              <div className="mt-8">
                {dailyMetrics.length > 0 ? (
                  <div>
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">
                          <TrendingUp className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                          <h3 className="text-lg font-medium">Usage Trends</h3>
                        </div>
                      </div>
                      
                      <div className="bg-card p-4 rounded-lg border shadow-sm">
                        <ResponsiveContainer width="100%" height={300}>
                          <LineChart data={dailyMetrics}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                            <XAxis
                              dataKey="date"
                              stroke="#9CA3AF"
                              tick={{ fill: '#9CA3AF' }}
                              tickFormatter={(value) => {
                                const date = new Date(value);
                                return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear().toString().slice(-2)}`;
                              }}
                            />
                            {/* Left Axis for Users */}
                            <YAxis
                              yAxisId="left"
                              stroke="#3B82F6"
                              tick={{ fill: '#3B82F6' }}
                              label={{ value: 'Unique Visitors', angle: -90, position: 'insideLeft', fill: '#3B82F6' }}
                            />
                            {/* Right Axis for Daily Submissions */}
                            <YAxis
                              yAxisId="right"
                              orientation="right"
                              stroke="#10B981"
                              tick={{ fill: '#10B981' }}
                              label={{ value: 'New Submissions', angle: 90, position: 'insideRight', fill: '#10B981' }}
                            />
                            <Tooltip
                              contentStyle={{
                                backgroundColor: '#1F2937',
                                border: '1px solid #374151',
                                borderRadius: '6px'
                              }}
                              labelStyle={{ color: '#F3F4F6' }}
                            />
                            <Legend
                              wrapperStyle={{ color: '#9CA3AF' }}
                            />
                            <Line
                              yAxisId="left"
                              type="monotone"
                              dataKey="users"
                              name="Unique Visitors"
                              stroke="#3B82F6"
                              strokeWidth={2}
                              dot={{ fill: '#3B82F6' }}
                              activeDot={{ r: 6 }}
                            />
                            <Line
                              yAxisId="right"
                              type="monotone"
                              dataKey="dailySubmissions"
                              name="New Submissions"
                              stroke="#10B981"
                              strokeWidth={2}
                              dot={{ fill: '#10B981' }}
                              activeDot={{ r: 6 }}
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                  </div>
                ) : (
                  <div className="bg-blue-50 dark:bg-blue-900/30 p-4 rounded-md">
                    <p className="text-sm text-muted-foreground">
                      <span className="font-medium">Usage Trends Chart:</span> No daily data available for the selected date range. The chart will appear once metrics are recorded.
                    </p>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              {isLoading ? 'Loading metrics...' : 'No metrics data available. Select a date range or filter to view metrics.'}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default UsageMetricsSection;
